/**
 * Dark Mode Handler
 * Supports theme setting default + session-based user preference
 */

(function() {
  const STORAGE_KEY = 'theme-preference';
  
  /**
   * Get the user's theme preference
   * Priority: sessionStorage > theme setting default
   */
  function getThemePreference() {
    // Check session storage first (user's choice for this session)
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    
    // Fall back to theme setting default
    const html = document.documentElement;
    return html.getAttribute('data-theme-default') || html.getAttribute('data-theme') || 'light';
  }

  /**
   * Apply the specified theme
   * @param {string} theme - 'light' or 'dark'
   * @param {boolean} save - Whether to save to session storage
   */
  function applyTheme(theme, save = false) {
    const html = document.documentElement;
    const body = document.body;
    
    // Remove existing theme classes
    body.classList.remove('theme-light', 'theme-dark');
    
    // Apply new theme
    html.setAttribute('data-theme', theme);
    body.classList.add('theme-' + theme);
    
    // Save to session if requested
    if (save) {
      sessionStorage.setItem(STORAGE_KEY, theme);
    }
    
    // Update toggle button icons
    updateToggleIcons(theme);
    
    // Dispatch custom event for other scripts
    window.dispatchEvent(new CustomEvent('themeApplied', {
      detail: { theme: theme }
    }));
  }

  /**
   * Toggle between light and dark mode
   */
  function toggleTheme() {
    const html = document.documentElement;
    const currentTheme = html.getAttribute('data-theme') || 'light';
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    applyTheme(newTheme, true);
  }

  /**
   * Update toggle button icons and labels based on current theme
   */
  function updateToggleIcons(theme) {
    const toggleButtons = document.querySelectorAll('[data-theme-toggle]');
    toggleButtons.forEach(btn => {
      const sunIcon = btn.querySelector('[data-icon="sun"]');
      const moonIcon = btn.querySelector('[data-icon="moon"]');
      const label = btn.querySelector('[data-theme-label]');
      
      if (sunIcon && moonIcon) {
        if (theme === 'dark') {
          sunIcon.style.display = 'block';
          moonIcon.style.display = 'none';
        } else {
          sunIcon.style.display = 'none';
          moonIcon.style.display = 'block';
        }
      }
      
      // Update text label if present
      if (label) {
        label.textContent = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
      }
    });
  }

  /**
   * Initialize theme toggle buttons
   */
  function initToggleButtons() {
    document.querySelectorAll('[data-theme-toggle]').forEach(btn => {
      if (btn._themeToggleInit) return;
      btn._themeToggleInit = true;
      
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        toggleTheme();
      });
    });
    
    // Update icons to match current theme
    const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
    updateToggleIcons(currentTheme);
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Store the default theme from settings
    const html = document.documentElement;
    const defaultTheme = html.getAttribute('data-theme') || 'light';
    html.setAttribute('data-theme-default', defaultTheme);
    
    // Apply user's preferred theme (may differ from default)
    const preferredTheme = getThemePreference();
    applyTheme(preferredTheme, false);
    
    // Initialize toggle buttons
    initToggleButtons();
    
    // Add smooth transitions after initial load
    setTimeout(function() {
      document.body.classList.add('theme-transitions-enabled');
    }, 100);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', initToggleButtons);

  // Expose API globally
  window.Theme = {
    apply: applyTheme,
    toggle: toggleTheme,
    get: function() {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
  };
})();

